import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageWidget = () => {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const widgetIdNum = Number(widgetId) || 0;

  // ---- State ----
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [widgetOrder, setWidgetOrder] = useState(0);

  const [widgetsType, setWidgetsType] = useState([]);  // list from API
  const [selectedWidgetType, setSelectedWidgetType] = useState(null); // number|null

  const [pages, setPages] = useState([]);              // list from API
  const [selectedPage, setSelectedPage] = useState(null);             // number|null

  // ---- react-select options ----
  const widgetTypeOptions = widgetsType.map((wt) => ({
    value: wt.Id,
    label: wt.DisplayName,
  }));

  const pageOptions = pages.map((p) => ({
    value: p.Id,
    label: p.Name,
  }));

  // Selected option objects derived from IDs
  const selectedWidgetTypeOption =
    widgetTypeOptions.find((o) => o.value === selectedWidgetType) || null;

  const selectedPageOption =
    pageOptions.find((o) => o.value === selectedPage) || null;

  // ---- Data load ----
  async function loadWidgetTypes() {
    const res = await fetch(`${Variables.API_URL}Rasha/WidgetType/Get`);
    if (res.ok) setWidgetsType(await res.json());
  }

  async function loadPages() {
    const res = await fetch(`${Variables.API_URL}Rasha/Page/Get`);
    if (res.ok) setPages(await res.json());
  }

  async function loadWidget() {
    const res = await fetch(`${Variables.API_URL}Rasha/Widget/Get/${widgetIdNum}`);
    if (!res.ok) return;
    const data = await res.json();

    setName(data.Name ?? "");
    setNameAr(data.NameAr ?? "");
    setIsPublished(Boolean(data.IsPublished));
    setWidgetOrder(Number(data.WidgetOrder) || 0);
    setSelectedPage(data.PageId ?? null);            // store ID directly
    setSelectedWidgetType(data.WidgetTypeId ?? null); // store ID directly
  }

  useEffect(() => {
    loadWidgetTypes();
    loadPages();
    if (widgetIdNum !== 0) loadWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetIdNum]);

  // ---- Validation ----
  function isValid() {
    if (!name.trim()) {
      toast.error("Widget Name is required");
      return false;
    }
    if (!selectedWidgetType) {
      toast.error("Widget Type is required");
      return false;
    }
    if (!selectedPage) {
      toast.error("Page is required");
      return false;
    }
    return true;
  }

  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      const payload = {
        id: widgetIdNum,
        name,
        widgetTypeId: selectedWidgetType, // ID
        isPublished,
        widgetOrder,
        pageId: selectedPage,             // ID
        nameAr,
      };

      const res = await fetch(`${Variables.API_URL}Rasha/Widget/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(widgetIdNum === 0 ? "Widget added successfully" : "Widget updated successfully");
      navigate("/admin/rasha/Widgets");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>{widgetIdNum === 0 ? "Add" : `Manage ${name}`} Widget</h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Widgets">
                  Widgets <AiOutlineRight />
                </Link>
              </li>
              <li>{widgetIdNum === 0 ? "Add" : "Manage"} Widget</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink
                    to="/admin/rasha/Widgets"
                    className="btn btn-secondary me-2"
                  >
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget Name <span className="required">*</span>
                    </label>
                    <input
                      id="name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Widget Title"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Widget Name (Arabic)</label>
                    <input
                      id="nameAr"
                      className="form-control"
                      value={nameAr}
                      onChange={(e) => setNameAr(e.target.value)}
                      placeholder="Widget Name Arabic"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget Order <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="widgetOrder"
                      className="form-control"
                      value={widgetOrder}
                      onChange={(e) => setWidgetOrder(Number(e.target.value) || 0)}
                      placeholder="Widget Order"
                    />
                  </div>

                  <div className="form-group col-lg-2 published-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget Type <span className="required">*</span>
                    </label>
                    <Select
                      options={widgetTypeOptions}
                      value={selectedWidgetTypeOption}
                      placeholder="Select Widget Type…"
                      onChange={(opt) => setSelectedWidgetType(opt ? opt.value : null)}
                      isClearable
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">
                      Page <span className="required">*</span>
                    </label>
                    <Select
                      options={pageOptions}
                      value={selectedPageOption}
                      placeholder="Select a page…"
                      onChange={(opt) => setSelectedPage(opt ? opt.value : null)}
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default ManageWidget;
